import { EEntityType, IBoundingBoxInfo, IRotatedBox, IRotatedBoxPure } from '@/editor/types';
import { IFurniture } from '../model/furniture';
import { IAssemblyModelInfo, IBoxModelInfo, IFurnitureModelInfo, IModelInfo, IMultiModelData } from './multiModel';
import { getFurnituresBoundingBox } from '../assembly/utils';
import { createPlacementMapper, getRotatedRectangleVertices, normalizeAngle } from '@/editor/math/utils';
import { getUniqueId } from '@/editor/utils';
import { cloneDeep } from 'lodash-es';
import { getSeparateWallBox } from '../separateWall/utils';

/**
 * 是否全是家具/组合。
 * 只用于选高度算法，刻意不复用交互层的 isMultiSelectFurnitureOrAssemblyOnly，
 * 那个守卫管的是「哪些多选允许变换/菜单」，改动它不应波及包围盒数学。
 */
function isFurnitureOrAssemblyOnly(
  modelInfos: IModelInfo[]
): modelInfos is (IFurnitureModelInfo | IAssemblyModelInfo)[] {
  return (
    modelInfos.length > 0 &&
    modelInfos.every((info) => info.type === EEntityType.Furniture || info.type === EEntityType.Assembly)
  );
}

/**
 * 把 modelInfo 归一成带旋转的盒数据。
 * 隔墙没有 width/rotation，且 center 是弧墙圆心而非盒中心，需要由轮廓推导。
 */
export function getModelInfoBox(modelInfo: IModelInfo): IRotatedBoxPure {
  if (modelInfo.type === EEntityType.SeparateWall) {
    return getSeparateWallBox(modelInfo.data);
  }

  const { data } = modelInfo;
  return {
    width: data.width,
    thickness: data.thickness,
    height: data.height,
    rotation: data.rotation,
    x: data.center.x,
    y: data.center.y,
    z: data.offGround + data.height / 2
  };
}

export function getMultiModelBoxInfo(modelInfos: IModelInfo[]) {
  const boxes = modelInfos.map(getModelInfoBox);
  const veticesData = boxes
    .map((box) => getRotatedRectangleVertices(box.width, box.thickness, { x: box.x, y: box.y }, box.rotation))
    .flat();

  const minX = Math.min(...veticesData.map((data) => data.x));
  const maxX = Math.max(...veticesData.map((data) => data.x));
  const minY = Math.min(...veticesData.map((data) => data.y));
  const maxY = Math.max(...veticesData.map((data) => data.y));
  const center = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  const width = maxX - minX;
  const thickness = maxY - minY;
  const rotation = 0;
  let height = 0;
  let offGround = 0;
  if (isFurnitureOrAssemblyOnly(modelInfos)) {
    // 家具/组合取真实模型的包围盒，而不是声明的 height
    let furnitureDatas: IFurniture[] = [];
    for (const modelInfo of modelInfos) {
      if (modelInfo.type === EEntityType.Furniture) {
        furnitureDatas.push(modelInfo.data);
      } else if (modelInfo.type === EEntityType.Assembly && modelInfo.subModeldatas.length) {
        furnitureDatas = furnitureDatas.concat(modelInfo.subModeldatas);
      }
    }

    const assemblyBoundingBox = getFurnituresBoundingBox(furnitureDatas);

    const min = assemblyBoundingBox.min;
    const max = assemblyBoundingBox.max;

    height = max.z - min.z;
    offGround = min.z;
  } else if (boxes.length) {
    // 门窗、隔墙以及混合选中，统一按盒的 z 范围计算，避免高度静默为 0
    const minZ = Math.min(...boxes.map((box) => box.z - box.height / 2));
    const maxZ = Math.max(...boxes.map((box) => box.z + box.height / 2));
    height = maxZ - minZ;
    offGround = minZ;
  }
  return {
    width,
    thickness,
    height,
    center,
    offGround,
    rotation
  };
}

interface IGetMultiModelBoxInfoBoxes extends IRotatedBoxPure {
  children?: IGetMultiModelBoxInfoBoxes[];
}
// 获取多模型的所有盒子数据
export function getMultiModelBoxes(params: {
  modelInfos: IModelInfo[];
  skipBoundingBoxCalculate?: boolean;
}): Required<IGetMultiModelBoxInfoBoxes> {
  const { modelInfos, skipBoundingBoxCalculate } = params;
  const containerBoundingBox: Required<IGetMultiModelBoxInfoBoxes> = {
    width: 0,
    thickness: 0,
    height: 0,
    rotation: 0,
    x: 0,
    y: 0,
    z: 0,
    children: []
  };

  // 计算子盒
  for (const modelInfo of modelInfos) {
    const childBox = getModelInfoBox(modelInfo);

    if (modelInfo.type === EEntityType.Assembly) {
      const assemblyBox = getMultiModelBoxes({
        modelInfos: modelInfo.subModeldatas.map((item) => ({
          id: item.id,
          type: EEntityType.Furniture,
          data: item,
          subModeldatas: []
        })),
        skipBoundingBoxCalculate: true
      });

      containerBoundingBox.children.push(...assemblyBox.children);
    }

    containerBoundingBox.children.push(childBox);
  }

  if (!skipBoundingBoxCalculate) {
    // 计算父盒
    const boundingBox = {
      minX: Infinity,
      maxX: -Infinity,
      minY: Infinity,
      maxY: -Infinity,
      minZ: Infinity,
      maxZ: -Infinity
    };
    containerBoundingBox.children.forEach((box) => {
      const veticesData = getRotatedRectangleVertices(box.width, box.thickness, { x: box.x, y: box.y }, box.rotation);

      boundingBox.minX = Math.min(...veticesData.map((data) => data.x), boundingBox.minX);
      boundingBox.maxX = Math.max(...veticesData.map((data) => data.x), boundingBox.maxX);
      boundingBox.minY = Math.min(...veticesData.map((data) => data.y), boundingBox.minY);
      boundingBox.maxY = Math.max(...veticesData.map((data) => data.y), boundingBox.maxY);
      boundingBox.minZ = Math.min(box.z - box.height / 2, boundingBox.minZ);
      boundingBox.maxZ = Math.max(box.z + box.height / 2, boundingBox.maxZ);
    });

    containerBoundingBox.width = boundingBox.maxX - boundingBox.minX;
    containerBoundingBox.thickness = boundingBox.maxY - boundingBox.minY;
    containerBoundingBox.height = boundingBox.maxZ - boundingBox.minZ;
    containerBoundingBox.x = (boundingBox.minX + boundingBox.maxX) / 2;
    containerBoundingBox.y = (boundingBox.minY + boundingBox.maxY) / 2;
    containerBoundingBox.z = (boundingBox.minZ + boundingBox.maxZ) / 2;
  }

  return containerBoundingBox;
}

export function createMultiModelData(modelInfos: IModelInfo[]): IMultiModelData {
  const { width, thickness, height, center, offGround, rotation } = getMultiModelBoxInfo(modelInfos);

  return {
    id: getUniqueId(),
    center,
    rotation,
    offGround,
    width,
    thickness,
    height,
    modelInfos
  };
}

export function getEntityBoxInfos(modelInfos: IModelInfo[]) {
  const boxInfos: IBoundingBoxInfo[] = [];
  for (const modelInfo of modelInfos) {
    if (modelInfo.type === EEntityType.Furniture || modelInfo.type === EEntityType.Assembly) {
      boxInfos.push({ type: modelInfo.type, data: modelInfo.data });
    }

    for (const subModeldata of modelInfo.subModeldatas) {
      boxInfos.push({ type: EEntityType.Furniture, data: subModeldata });
    }
  }

  return boxInfos;
}

/**
 * 隔墙由起止点定义，没有 center/rotation，不支持整体移动。
 * 含隔墙时返回 null，调用方应整体拒绝移动，而不是移动其余部分。
 */
export function toBoxModelInfos(modelInfos: IModelInfo[]): IBoxModelInfo[] | null {
  const boxModelInfos: IBoxModelInfo[] = [];
  for (const modelInfo of modelInfos) {
    if (modelInfo.type === EEntityType.SeparateWall) return null;
    boxModelInfos.push(modelInfo);
  }

  return boxModelInfos;
}

export function adsorbModelInfos(modelInfos: IModelInfo[], adsorbBox: IRotatedBox, originBox: IRotatedBox) {
  // 含隔墙时整体不移动，原样返回
  const boxModelInfos = toBoxModelInfos(modelInfos);
  if (!boxModelInfos) return modelInfos;

  const mapPlacement = createPlacementMapper(originBox, adsorbBox);

  const adsorbModeInfos: IModelInfo[] = [];
  for (const modelInfo of boxModelInfos) {
    const adsorbModelInfo = cloneDeep(modelInfo);
    const modelData = adsorbModelInfo.data;

    const { center, rotation } = mapPlacement(modelData);
    modelData.center = center;
    modelData.rotation = normalizeAngle(rotation);
    adsorbModeInfos.push(adsorbModelInfo);

    for (const subModeldata of adsorbModelInfo.subModeldatas) {
      const { center: subCenter, rotation: subRotation } = mapPlacement(subModeldata);
      subModeldata.center = subCenter;
      subModeldata.rotation = normalizeAngle(subRotation);
    }
  }

  return adsorbModeInfos;
}
